import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { updateContentDnaSchema } from "@shared/schema";
import type { UpdateContentDna, CompanyProfile } from "@shared/schema";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Lightbulb, MessageSquare, Target, BarChart3, Save, CheckCircle2, Plus, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import React from "react";
import { StrategyRefineSection } from "@/components/briefing/StrategyRefineSection";
import { hotelBriefingSchema } from "@shared/hotelBriefing";
import { useIsHotelEdition } from "@/lib/hotelEdition";

const BRAND_VOICE_SLIDER_PAIRS = [
  { key: 'professional_casual', leftLabel: 'Professionell', rightLabel: 'Locker' },
  { key: 'direct_empathetic', leftLabel: 'Direkt', rightLabel: 'Einfühlsam' },
  { key: 'factual_emotional', leftLabel: 'Sachlich', rightLabel: 'Emotional' },
  { key: 'formal_conversational', leftLabel: 'Formal', rightLabel: 'Gesprächig' },
] as const;

const TOPICS_OF_INTEREST_OPTIONS = [
  { value: "tools_tech", label: "Tools & Technologie" },
  { value: "marketing", label: "Marketing & Strategie" },
  { value: "social_trends", label: "Social Media Trends" },
  { value: "industry_news", label: "Branchennews" },
  { value: "best_practices", label: "Best Practices" },
  { value: "case_studies", label: "Case Studies" },
];

const TOPICS_TO_AVOID_OPTIONS = [
  { value: "politics", label: "Politik" },
  { value: "religion", label: "Religion" },
  { value: "controversial", label: "Kontroverse Themen" },
  { value: "competitors", label: "Wettbewerber" },
];

const agencyResolver = zodResolver(updateContentDnaSchema);

export default function ATeamBriefing() {
  const { toast } = useToast();

  const { data: company, isLoading } = useQuery<CompanyProfile>({
    queryKey: ['/api/companies/me']
  });

  // Hotel-Edition (PLAN §5.1 item 5, Mike A7): hotels see only "Ihre Strategie schärfen", the tone and the
  // topics to avoid; the B2B parts (example posts, interests, content mix) are neither shown nor required.
  const isHotel = useIsHotelEdition();
  const isHotelRef = React.useRef(isHotel);
  isHotelRef.current = isHotel;

  const form = useForm<UpdateContentDna>({
    resolver: (values, context, options) =>
      isHotelRef.current
        ? (zodResolver(hotelBriefingSchema) as unknown as typeof agencyResolver)(values, context, options)
        : agencyResolver(values, context, options),
    defaultValues: {
      brandVoiceTones: {
        professional_casual: 50,
        direct_empathetic: 50,
        factual_emotional: 50,
        formal_conversational: 50,
      },
      examplePosts: {
        linkedin: [],
        instagram: [],
        facebook: [],
        youtube: [],
      },
      specificNiche: "",
      topicsOfInterest: [],
      newsletters: [],
      topicsToAvoid: [],
      topicsToAvoidOther: "",
      postingFrequency: {
        linkedin: 0,
        instagram: 0,
        facebook: 0,
        youtube: 0,
      },
      contentMix: {
        educational: 0,
        thoughtLeadership: 0,
        behindTheScenes: 0,
        promotional: 0,
      },
      evergreenTopics: [],
    },
  });

  // keepDirtyValues: a background refresh of the company (hotel capability polling) keeps unsaved edits.
  React.useEffect(() => {
    if (company) {
      form.reset({
        brandVoiceTones: company.brandVoiceTones || {
          professional_casual: 50,
          direct_empathetic: 50,
          factual_emotional: 50,
          formal_conversational: 50,
        },
        examplePosts: company.examplePosts || {
          linkedin: [],
          instagram: [],
          facebook: [],
          youtube: [],
        },
        specificNiche: company.specificNiche || "",
        topicsOfInterest: company.topicsOfInterest || [],
        newsletters: company.newsletters || [],
        topicsToAvoid: company.topicsToAvoid || [],
        topicsToAvoidOther: company.topicsToAvoidOther || "",
        postingFrequency: company.postingFrequency || {
          linkedin: 0,
          instagram: 0,
          facebook: 0,
          youtube: 0,
        },
        contentMix: company.contentMix || {
          educational: 0,
          thoughtLeadership: 0,
          behindTheScenes: 0,
          promotional: 0,
        },
        evergreenTopics: company.evergreenTopics || [],
      }, { keepDirtyValues: true });
    }
  }, [company, form]);

  const submittedRef = React.useRef<UpdateContentDna | null>(null);

  const updateContentDnaMutation = useMutation({
    mutationFn: async (data: UpdateContentDna) => {
      return await apiRequest('PATCH', '/api/companies/me/content-dna', data);
    },
    onSuccess: () => {
      // Saved: mark the submitted values clean so the refetch below adopts the server values (keepDirtyValues
      // above). A field edited while the request ran keeps what was typed and stays dirty (review he-a R2-3).
      const submitted = submittedRef.current;
      if (submitted) {
        const current = form.getValues();
        form.reset(submitted);
        for (const key of Object.keys(current) as (keyof UpdateContentDna)[]) {
          if (JSON.stringify(current[key]) !== JSON.stringify(submitted[key])) {
            form.setValue(key, current[key] as never, { shouldDirty: true });
          }
        }
      }
      queryClient.invalidateQueries({ queryKey: ['/api/companies/me'] });
      
      // Trigger Voice Profile webhook to N8N (fire-and-forget, non-critical)
      fetch('/api/webhooks/trigger-voice-profile', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include'
      }).catch(() => {
        // Silently ignore webhook errors (non-critical, fire-and-forget)
      });
      
      toast({
        title: "Gespeichert!",
        description: "Ihr A-Team Briefing wurde gespeichert.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Fehler",
        description: error.message || "Beim Speichern ist ein Fehler aufgetreten.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: UpdateContentDna) => {
    submittedRef.current = form.getValues();
    updateContentDnaMutation.mutate(data);
  };

  const calculateProgress = React.useMemo(() => {
    const formData = form.watch();

    // Section 1: Brand Voice - always complete (has default values)
    const section1Complete = true;

    const totalValidPosts = Object.values(formData.examplePosts || {})
      .flat()
      .filter((post: any) => post?.url || post?.text).length;
    const section2Complete = totalValidPosts >= 2;

    const mixTotal =
      (formData.contentMix?.educational || 0) +
      (formData.contentMix?.thoughtLeadership || 0) +
      (formData.contentMix?.behindTheScenes || 0) +
      (formData.contentMix?.promotional || 0);
    const section4Complete = mixTotal === 100;

    const section3Filled =
      !!formData.specificNiche ||
      (formData.topicsOfInterest && formData.topicsOfInterest.length > 0) ||
      (formData.newsletters && formData.newsletters.filter(n => n).length > 0) ||
      (formData.topicsToAvoid && formData.topicsToAvoid.length > 0) ||
      !!formData.topicsToAvoidOther;

    const requiredComplete = [section1Complete, section2Complete, section4Complete].filter(Boolean).length;

    return {
      section1Complete,
      section2Complete,
      section3Filled,
      section4Complete,
      requiredComplete,
      totalRequired: 3,
    };
  }, [form.watch()]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Lade Briefing...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container max-w-5xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">A-Team Briefing</h1>
        <p className="text-muted-foreground text-lg">
          Legen Sie die Content-DNA Ihres Hotels fest, damit Ihre Beiträge nach Ihnen klingen.
        </p>
      </div>

      <StrategyRefineSection company={company} />

      {!isHotel && <Card className="mb-6" data-testid="card-progress">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Erforderlich:</span>
                <Badge
                  variant={calculateProgress.requiredComplete === 3 ? "default" : "secondary"}
                  data-testid="badge-required-progress"
                >
                  {calculateProgress.requiredComplete}/{calculateProgress.totalRequired}
                </Badge>
              </div>
              <Separator orientation="vertical" className="h-6" />
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Optional:</span>
                <Badge
                  variant={calculateProgress.section3Filled ? "default" : "outline"}
                  data-testid="badge-optional-progress"
                >
                  {calculateProgress.section3Filled ? "Ausgefüllt" : "Leer"}
                </Badge>
              </div>
            </div>
            {company?.contentDnaCompleted && (
              <Badge variant="default" className="gap-1" data-testid="badge-content-dna-completed">
                <CheckCircle2 className="h-3 w-3" />
                Vollständig
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>}

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
        {/* Section 1: Brand Voice */}
        <Card data-testid="card-brand-voice">
          <CardHeader>
            <div className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-primary" />
              <CardTitle>{isHotel ? "1. Tonalität" : "1. Brand Voice & Tonalität"}</CardTitle>
            </div>
            <CardDescription>
              Wie möchten Sie wahrgenommen werden? Bewegen Sie die Regler zur Seite, die besser zu Ihrem Haus passt.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {BRAND_VOICE_SLIDER_PAIRS.map(({ key, leftLabel, rightLabel }) => (
                <div key={key} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium">{leftLabel}</span>
                    <span className="text-muted-foreground">
                      {form.watch(`brandVoiceTones.${key}`) ?? 50}%
                    </span>
                    <span className="font-medium">{rightLabel}</span>
                  </div>
                  <Input
                    type="range"
                    min="0"
                    max="100"
                    step="5"
                    {...form.register(`brandVoiceTones.${key}`, { valueAsNumber: true })}
                    data-testid={`slider-brand-voice-${key}`}
                  />
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <span>◄ {leftLabel}</span>
                    <span>{rightLabel} ►</span>
                  </div>
                </div>
              ))}
              {form.formState.errors.brandVoiceTones && (
                <p className="text-sm text-destructive">
                  {String(form.formState.errors.brandVoiceTones.message || "Brand Voice Fehler")}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Section 2: Example Posts */}
        {!isHotel && <Card data-testid="card-example-posts">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-primary" />
              <CardTitle>2. Beispiel-Posts</CardTitle>
            </div>
            <CardDescription>
              Fügen Sie mindestens 2 Beispiel-Posts hinzu (insgesamt über alle Plattformen). Jeder Post braucht entweder eine URL oder Text.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="linkedin" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="linkedin">LinkedIn</TabsTrigger>
                <TabsTrigger value="instagram">Instagram</TabsTrigger>
                <TabsTrigger value="facebook">Facebook</TabsTrigger>
                <TabsTrigger value="youtube">YouTube</TabsTrigger>
              </TabsList>
              {(['linkedin', 'instagram', 'facebook', 'youtube'] as const).map((platform) => (
                <TabsContent key={platform} value={platform} className="space-y-4 mt-4">
                  <Controller
                    control={form.control}
                    name={`examplePosts.${platform}`}
                    render={({ field }) => {
                      const posts = field.value || [];
                      return (
                        <div className="space-y-3">
                          {posts.map((_, index) => (
                            <div key={index} className="p-4 border rounded-md space-y-3">
                              <div className="flex items-center justify-between">
                                <Label className="text-sm font-medium">Post {index + 1}</Label>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => {
                                    const newPosts = posts.filter((_, i) => i !== index);
                                    field.onChange(newPosts);
                                  }}
                                  data-testid={`button-remove-post-${platform}-${index}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                              <div>
                                <Label className="text-sm">URL (optional)</Label>
                                <Input
                                  placeholder="https://..."
                                  value={posts[index]?.url || ""}
                                  onChange={(e) => {
                                    const newPosts = [...posts];
                                    newPosts[index] = { ...newPosts[index], url: e.target.value };
                                    field.onChange(newPosts);
                                  }}
                                  data-testid={`input-post-url-${platform}-${index}`}
                                />
                              </div>
                              <div>
                                <Label className="text-sm">Text (optional)</Label>
                                <Textarea
                                  placeholder="Post-Inhalt..."
                                  value={posts[index]?.text || ""}
                                  onChange={(e) => {
                                    const newPosts = [...posts];
                                    newPosts[index] = { ...newPosts[index], text: e.target.value };
                                    field.onChange(newPosts);
                                  }}
                                  rows={3}
                                  data-testid={`textarea-post-text-${platform}-${index}`}
                                />
                              </div>
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              field.onChange([...posts, { url: "", text: "" }]);
                            }}
                            data-testid={`button-add-post-${platform}`}
                          >
                            <Plus className="h-4 w-4 mr-2" />
                            Post hinzufügen
                          </Button>
                        </div>
                      );
                    }}
                  />
                </TabsContent>
              ))}
            </Tabs>
            
            {/* Total Posts Count & Validation */}
            <div className="mt-4">
              {(() => {
                const formData = form.watch();
                const totalValidPosts = Object.values(formData.examplePosts || {})
                  .flat()
                  .filter((post: any) => post?.url || post?.text).length;
                
                if (totalValidPosts < 2) {
                  return (
                    <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 border border-destructive text-destructive text-sm" data-testid="error-example-posts">
                      <span>⚠</span>
                      <span>Mindestens 2 Beispiel-Posts erforderlich (aktuell: {totalValidPosts})</span>
                    </div>
                  );
                }
                return (
                  <p className="text-sm text-muted-foreground">
                    ✓ {totalValidPosts} Beispiel-Posts hinzugefügt
                  </p>
                );
              })()}
            </div>
            
            {form.formState.errors.examplePosts && (
              <p className="text-sm text-destructive mt-2">
                {form.formState.errors.examplePosts.message}
              </p>
            )}
          </CardContent>
        </Card>}

        {/* Section 3: Industry & Interests */}
        <Card data-testid="card-industry-interests">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Target className="h-5 w-5 text-primary" />
              <CardTitle>{isHotel ? "2. Themen, die wir vermeiden" : "3. Branche & Interessen"}</CardTitle>
            </div>
            <CardDescription>
              {isHotel
                ? "Worüber Ihr Haus nicht schreiben möchte. Ihr Team hält sich daran."
                : "Legen Sie Ihre Nische und die wichtigen Themen für Ihre Content-Strategie fest."}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {!isHotel && <>
            {/* Specific Niche */}
            <div>
              <Label htmlFor="specificNiche">Spezifische Nische (optional)</Label>
              <Input
                id="specificNiche"
                placeholder="z.B. Boutique-Hotels in Bergregionen, Event-Marketing für Tech-Startups"
                {...form.register("specificNiche")}
                data-testid="input-specific-niche"
              />
              <p className="text-sm text-muted-foreground mt-1">
                Beschreiben Sie Ihre genaue Marktnische für gezieltere Inhalte
              </p>
            </div>

            {/* Topics of Interest */}
            <div className="space-y-3">
              <Label>Welche Themen soll Ihr A-Team für Sie beobachten? (optional)</Label>
              <Controller
                control={form.control}
                name="topicsOfInterest"
                render={({ field }) => (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {TOPICS_OF_INTEREST_OPTIONS.map((option) => (
                      <div
                        key={option.value}
                        className="flex items-center space-x-2 rounded-md border p-3 hover-elevate active-elevate-2"
                      >
                        <Checkbox
                          id={`topic-${option.value}`}
                          checked={field.value?.includes(option.value)}
                          onCheckedChange={(checked) => {
                            const current = field.value || [];
                            if (checked) {
                              field.onChange([...current, option.value]);
                            } else {
                              field.onChange(current.filter((v) => v !== option.value));
                            }
                          }}
                          data-testid={`checkbox-topic-${option.value}`}
                        />
                        <Label htmlFor={`topic-${option.value}`} className="flex-1 cursor-pointer font-normal">
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                )}
              />
            </div>

            {/* Newsletters */}
            <div className="space-y-3">
              <Label>Welche Newsletter oder Publikationen nutzen Sie als Inspiration? (optional)</Label>
              <Controller
                control={form.control}
                name="newsletters"
                render={({ field }) => {
                  const newsletters = field.value || [];
                  return (
                    <div className="space-y-2">
                      {newsletters.map((newsletter, index) => (
                        <div key={index} className="flex gap-2">
                          <Input
                            placeholder="z.B. 'Marketing Week'"
                            value={newsletter}
                            onChange={(e) => {
                              const newNewsletters = [...newsletters];
                              newNewsletters[index] = e.target.value;
                              field.onChange(newNewsletters);
                            }}
                            data-testid={`input-newsletter-${index}`}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              field.onChange(newsletters.filter((_, i) => i !== index));
                            }}
                            data-testid={`button-remove-newsletter-${index}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => field.onChange([...newsletters, ""])}
                        data-testid="button-add-newsletter"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Newsletter hinzufügen
                      </Button>
                    </div>
                  );
                }}
              />
            </div>

            </>}

            {/* Topics to Avoid */}
            <div className="space-y-3">
              <Label>Themen zu vermeiden (optional)</Label>
              <Controller
                control={form.control}
                name="topicsToAvoid"
                render={({ field }) => (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {TOPICS_TO_AVOID_OPTIONS.map((option) => (
                      <div
                        key={option.value}
                        className="flex items-center space-x-2 rounded-md border p-3 hover-elevate active-elevate-2"
                      >
                        <Checkbox
                          id={`avoid-${option.value}`}
                          checked={field.value?.includes(option.value)}
                          onCheckedChange={(checked) => {
                            const current = field.value || [];
                            if (checked) {
                              field.onChange([...current, option.value]);
                            } else {
                              field.onChange(current.filter((v) => v !== option.value));
                            }
                          }}
                          data-testid={`checkbox-avoid-${option.value}`}
                        />
                        <Label htmlFor={`avoid-${option.value}`} className="flex-1 cursor-pointer font-normal">
                          {option.label}
                        </Label>
                      </div>
                    ))}
                  </div>
                )}
              />
              <Input
                placeholder="Andere Themen zu vermeiden..."
                {...form.register("topicsToAvoidOther")}
                data-testid="input-topics-avoid-other"
              />
              {form.formState.errors.topicsToAvoidOther && (
                <p className="text-sm text-destructive" data-testid="error-topics-avoid-other">
                  {isHotel ? "Bitte fassen Sie sich kürzer (höchstens 500 Zeichen)." : String(form.formState.errors.topicsToAvoidOther.message)}
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Section 4: Content Strategy */}
        {!isHotel && <Card data-testid="card-content-strategy">
          <CardHeader>
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              <CardTitle>4. Content-Strategie</CardTitle>
            </div>
            <CardDescription>
              Legen Sie Ihre Posting-Frequenz und Ihren Content-Mix fest (zusammen 100 %).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Posting Frequency */}
            <div className="space-y-3">
              <Label>Posting-Frequenz pro Woche (optional)</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {(['linkedin', 'instagram', 'facebook', 'youtube'] as const).map((platform) => (
                  <div key={platform}>
                    <Label htmlFor={`freq-${platform}`} className="text-sm capitalize">
                      {platform}
                    </Label>
                    <Controller
                      control={form.control}
                      name={`postingFrequency.${platform}`}
                      render={({ field }) => (
                        <Select
                          value={field.value?.toString() || "0"}
                          onValueChange={(value) => field.onChange(parseInt(value))}
                        >
                          <SelectTrigger id={`freq-${platform}`} data-testid={`select-frequency-${platform}`}>
                            <SelectValue placeholder="Bitte wählen..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">Bitte wählen...</SelectItem>
                            <SelectItem value="1">1 Post pro Woche</SelectItem>
                            <SelectItem value="2">2 Posts pro Woche</SelectItem>
                            <SelectItem value="3">3 Posts pro Woche</SelectItem>
                            <SelectItem value="4">4 Posts pro Woche</SelectItem>
                            <SelectItem value="5">5 Posts pro Woche</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Content Mix Sliders */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label>Content Mix (muss 100% ergeben)</Label>
                <Controller
                  control={form.control}
                  name="contentMix"
                  render={({ field }) => {
                    const total =
                      (field.value?.educational || 0) +
                      (field.value?.thoughtLeadership || 0) +
                      (field.value?.behindTheScenes || 0) +
                      (field.value?.promotional || 0);
                    const isValid = total === 100;
                    return (
                      <Badge variant={isValid ? "default" : "destructive"} data-testid="badge-content-mix-total">
                        {total}% {isValid ? "✓" : ""}
                      </Badge>
                    );
                  }}
                />
              </div>

              {/* Manual Sliders with Dynamic Max Values - Lock System */}
              
              {/* Slider 1: Educational (max = 100%) */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="mix-educational" className="text-sm">
                    Bildungsinhalte
                  </Label>
                  <span className="text-sm font-medium" data-testid="text-mix-educational">
                    {form.watch('contentMix.educational') ?? 0}%
                  </span>
                </div>
                <Input
                  id="mix-educational"
                  type="range"
                  min="0"
                  max="100"
                  step="5"
                  {...form.register('contentMix.educational', {
                    valueAsNumber: true,
                    onChange: () => {
                      const educational = form.getValues('contentMix.educational') || 0;
                      const thoughtLeadership = form.getValues('contentMix.thoughtLeadership') || 0;
                      const behindTheScenes = form.getValues('contentMix.behindTheScenes') || 0;
                      
                      // Clamp thoughtLeadership to new max
                      const maxThought = 100 - educational;
                      if (thoughtLeadership > maxThought) {
                        form.setValue('contentMix.thoughtLeadership', maxThought);
                      }
                      
                      // Clamp behindTheScenes to new max
                      const maxBehind = 100 - educational - form.getValues('contentMix.thoughtLeadership');
                      if (behindTheScenes > maxBehind) {
                        form.setValue('contentMix.behindTheScenes', maxBehind);
                      }
                      
                      // Recalculate promotional
                      const finalEducational = form.getValues('contentMix.educational') || 0;
                      const finalThought = form.getValues('contentMix.thoughtLeadership') || 0;
                      const finalBehind = form.getValues('contentMix.behindTheScenes') || 0;
                      const autoPromotional = 100 - finalEducational - finalThought - finalBehind;
                      form.setValue('contentMix.promotional', autoPromotional);
                    }
                  })}
                  data-testid="slider-mix-educational"
                />
              </div>

              {/* Slider 2: Thought Leadership (max = 100% - educational) */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="mix-thoughtLeadership" className="text-sm">
                    Thought Leadership
                  </Label>
                  <span className="text-sm font-medium" data-testid="text-mix-thoughtLeadership">
                    {form.watch('contentMix.thoughtLeadership') ?? 0}%
                  </span>
                </div>
                <Input
                  id="mix-thoughtLeadership"
                  type="range"
                  min="0"
                  max={100 - (form.watch('contentMix.educational') ?? 0)}
                  step="5"
                  {...form.register('contentMix.thoughtLeadership', {
                    valueAsNumber: true,
                    onChange: () => {
                      const educational = form.getValues('contentMix.educational') || 0;
                      const thoughtLeadership = form.getValues('contentMix.thoughtLeadership') || 0;
                      const behindTheScenes = form.getValues('contentMix.behindTheScenes') || 0;
                      
                      // Clamp behindTheScenes to new max
                      const maxBehind = 100 - educational - thoughtLeadership;
                      if (behindTheScenes > maxBehind) {
                        form.setValue('contentMix.behindTheScenes', maxBehind);
                      }
                      
                      // Recalculate promotional
                      const finalEducational = form.getValues('contentMix.educational') || 0;
                      const finalThought = form.getValues('contentMix.thoughtLeadership') || 0;
                      const finalBehind = form.getValues('contentMix.behindTheScenes') || 0;
                      const autoPromotional = 100 - finalEducational - finalThought - finalBehind;
                      form.setValue('contentMix.promotional', autoPromotional);
                    }
                  })}
                  data-testid="slider-mix-thoughtLeadership"
                />
              </div>

              {/* Slider 3: Behind the Scenes (max = 100% - educational - thoughtLeadership) */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="mix-behindTheScenes" className="text-sm">
                    Behind the Scenes
                  </Label>
                  <span className="text-sm font-medium" data-testid="text-mix-behindTheScenes">
                    {form.watch('contentMix.behindTheScenes') ?? 0}%
                  </span>
                </div>
                <Input
                  id="mix-behindTheScenes"
                  type="range"
                  min="0"
                  max={100 - (form.watch('contentMix.educational') ?? 0) - (form.watch('contentMix.thoughtLeadership') ?? 0)}
                  step="5"
                  {...form.register('contentMix.behindTheScenes', {
                    valueAsNumber: true,
                    onChange: () => {
                      const educational = form.getValues('contentMix.educational') || 0;
                      const thoughtLeadership = form.getValues('contentMix.thoughtLeadership') || 0;
                      const behindTheScenes = form.getValues('contentMix.behindTheScenes') || 0;
                      
                      // Recalculate promotional
                      const autoPromotional = 100 - educational - thoughtLeadership - behindTheScenes;
                      form.setValue('contentMix.promotional', autoPromotional);
                    }
                  })}
                  data-testid="slider-mix-behindTheScenes"
                />
              </div>

              {/* Auto-calculated 4th slider (promotional) - Read-only display */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-sm">
                    Werbung / Promotion
                  </Label>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium" data-testid="text-mix-promotional">
                      {form.watch('contentMix.promotional') ?? 0}%
                    </span>
                    <Badge variant="outline" className="text-xs">
                      Automatisch berechnet
                    </Badge>
                  </div>
                </div>
                <div className="relative h-2 bg-secondary rounded-full overflow-hidden" data-testid="display-mix-promotional">
                  <div 
                    className="absolute top-0 left-0 h-full bg-primary transition-all duration-200"
                    style={{ width: `${form.watch('contentMix.promotional') ?? 0}%` }}
                  />
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>0%</span>
                  <span>100%</span>
                </div>
              </div>

              {form.formState.errors.contentMix && (
                <p className="text-sm text-destructive">
                  {form.formState.errors.contentMix.message}
                </p>
              )}
            </div>

            {/* Evergreen Topics */}
            <div className="space-y-3">
              <Label>Über welche Themen möchte Ihr A-Team regelmäßig Content erstellen? (optional)</Label>
              <p className="text-sm text-muted-foreground">
                Nennen Sie 3-5 Kern-Themen - Ihre Expertise, Leidenschaften oder Painpoints Ihrer Branche
              </p>
              <Controller
                control={form.control}
                name="evergreenTopics"
                render={({ field }) => {
                  const topics = field.value || [];
                  return (
                    <div className="space-y-2">
                      {topics.map((topic, index) => (
                        <div key={index} className="flex gap-2">
                          <Input
                            placeholder="z.B. 'Effiziente Team-Führung'"
                            value={topic}
                            onChange={(e) => {
                              const newTopics = [...topics];
                              newTopics[index] = e.target.value;
                              field.onChange(newTopics);
                            }}
                            data-testid={`input-evergreen-${index}`}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              field.onChange(topics.filter((_, i) => i !== index));
                            }}
                            data-testid={`button-remove-evergreen-${index}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => field.onChange([...topics, ""])}
                        data-testid="button-add-evergreen"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Topic hinzufügen
                      </Button>
                    </div>
                  );
                }}
              />
            </div>
          </CardContent>
        </Card>}

        {/* Save Button */}
        <div className="flex items-center justify-between pt-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {!isHotel && company?.contentDnaCompleted && (
              <>
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span>Briefing vollständig ausgefüllt</span>
              </>
            )}
          </div>
          <Button
            type="submit"
            size="lg"
            disabled={updateContentDnaMutation.isPending || (!isHotel && !calculateProgress.section4Complete)}
            data-testid="button-save-briefing"
          >
            <Save className="h-4 w-4 mr-2" />
            {updateContentDnaMutation.isPending ? "Speichert..." : "A-Team Briefing speichern"}
          </Button>
        </div>
      </form>
    </div>
  );
}
